import React, { useState, useEffect } from 'react';
import { Save, X, Calendar, Tag, Image, FileText, Download, Star } from 'lucide-react';

const ResourceEditor = ({ resource = null, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    id: resource?.id || Date.now(),
    title: resource?.title || '',
    description: resource?.description || '',
    category: resource?.category || 'Documentos',
    type: resource?.type || 'document',
    format: resource?.format || 'PDF',
    size: resource?.size || '',
    downloadUrl: resource?.downloadUrl || '#',
    date: resource?.date || new Date().toISOString().split('T')[0],
    downloads: resource?.downloads || 0,
    featured: resource?.featured || false,
    tags: resource?.tags || [],
    preview: resource?.preview || ''
  });

  // Actualizar formData cuando cambie la prop resource
  useEffect(() => {
    if (resource) {
      setFormData({
        id: resource.id || Date.now(),
        title: resource.title || '',
        description: resource.description || '',
        category: resource.category || 'Documentos',
        type: resource.type || 'document',
        format: resource.format || 'PDF',
        size: resource.size || '',
        downloadUrl: resource.downloadUrl || '#',
        date: resource.date || new Date().toISOString().split('T')[0],
        downloads: resource.downloads || 0,
        featured: resource.featured || false,
        tags: resource.tags || [],
        preview: resource.preview || ''
      });
    } else {
      // Resetear para nuevo elemento
      setFormData({
        id: Date.now(),
        title: '',
        description: '',
        category: 'Documentos',
        type: 'document',
        format: 'PDF',
        size: '',
        downloadUrl: '#',
        date: new Date().toISOString().split('T')[0],
        downloads: 0,
        featured: false,
        tags: [],
        preview: ''
      });
    }
  }, [resource]);

  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState({});

  const categories = ['Guías', 'Plantillas', 'Instrumentos', 'Manuales', 'Infografías', 'Videos', 'Documentos'];
  const types = ['document', 'template', 'tool', 'manual', 'infographic', 'video', 'guide'];
  const formats = ['PDF', 'DOCX', 'XLSX', 'PNG', 'JPG', 'MP4', 'ZIP', 'PPTX'];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Limpiar error del campo cuando se edita
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  const validate = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) newErrors.title = 'El título es requerido';
    else if (formData.title.length < 10) newErrors.title = 'El título debe tener al menos 10 caracteres';
    else if (formData.title.length > 200) newErrors.title = 'El título no puede exceder 200 caracteres';
    
    if (!formData.description.trim()) newErrors.description = 'La descripción es requerida';
    else if (formData.description.length < 20) newErrors.description = 'La descripción debe tener al menos 20 caracteres';
    else if (formData.description.length > 500) newErrors.description = 'La descripción no puede exceder 500 caracteres';
    
    if (!formData.date) newErrors.date = 'La fecha es requerida';
    
    if (formData.size && !isValidSize(formData.size)) {
      newErrors.size = 'Formato de tamaño inválido (ej: 1.2 MB)';
    }

    if (formData.preview && !isValidUrl(formData.preview)) {
      newErrors.preview = 'La URL de vista previa no es válida';
    }

    if (formData.downloads < 0) {
      newErrors.downloads = 'El número de descargas no puede ser negativo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const isValidSize = (size) => {
    return /^\d+(\.\d+)?\s*(KB|MB|GB)$/i.test(size);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSave(formData);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      handleSubmit(e);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6 border-b border-gray-200">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Download className="w-5 h-5 text-burgundy-600" />
          {resource ? 'Editar Recurso' : 'Nuevo Recurso'}
        </h3>
      </div>

      <form onSubmit={handleSubmit} className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Columna izquierda */}
          <div className="space-y-6">
            {/* Título */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Título *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                onKeyPress={handleKeyPress}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500 ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Título del recurso"
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>

            {/* Descripción */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descripción *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500 ${
                  errors.description ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Descripción detallada del recurso"
              />
              <p className="text-sm text-gray-500 mt-1">
                {formData.description.length}/500 caracteres
              </p>
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>

            {/* URL de descarga */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL de descarga
              </label>
              <input
                type="text"
                value={formData.downloadUrl}
                onChange={(e) => handleChange('downloadUrl', e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                placeholder="URL donde se puede descargar el recurso"
              />
            </div>

            {/* Vista previa */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL de vista previa
              </label>
              <input
                type="url"
                value={formData.preview}
                onChange={(e) => handleChange('preview', e.target.value)}
                onKeyPress={handleKeyPress}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500 ${
                  errors.preview ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="https://ejemplo.com/preview.jpg"
              />
              {errors.preview && <p className="text-red-500 text-sm mt-1">{errors.preview}</p>}
              {formData.preview && isValidUrl(formData.preview) && (
                <div className="mt-2">
                  <img 
                    src={formData.preview} 
                    alt="Vista previa" 
                    className="w-full h-32 object-cover rounded-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Columna derecha */}
          <div className="space-y-6">
            {/* Categoría y Tipo */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoría *
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                >
                  {types.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Formato y Tamaño */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Formato *
                </label>
                <select
                  value={formData.format}
                  onChange={(e) => handleChange('format', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                >
                  {formats.map(format => (
                    <option key={format} value={format}>{format}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tamaño
                </label>
                <input
                  type="text"
                  value={formData.size}
                  onChange={(e) => handleChange('size', e.target.value)}
                  onKeyPress={handleKeyPress}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500 ${
                    errors.size ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="ej: 2.5 MB"
                />
                {errors.size && <p className="text-red-500 text-sm mt-1">{errors.size}</p>}
              </div>
            </div>

            {/* Fecha y Descargas */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500 ${
                    errors.date ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descargas
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.downloads}
                  onChange={(e) => handleChange('downloads', parseInt(e.target.value) || 0)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500 ${
                    errors.downloads ? 'border-red-500' : 'border-gray-300'
                  }`}
                  placeholder="0"
                />
                {errors.downloads && <p className="text-red-500 text-sm mt-1">{errors.downloads}</p>}
              </div>
            </div>

            {/* Destacado */}
            <div className="flex items-center">
              <input
                type="checkbox"
                id="featured"
                checked={formData.featured}
                onChange={(e) => handleChange('featured', e.target.checked)}
                className="w-4 h-4 text-burgundy-600 border-gray-300 rounded focus:ring-burgundy-500"
              />
              <label htmlFor="featured" className="ml-2 text-sm font-medium text-gray-700 flex items-center gap-1">
                <Star className="w-4 h-4" />
                Recurso destacado
              </label>
            </div>

            {/* Etiquetas */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Etiquetas
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={newTag}
                  onChange={(e) => setNewTag(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addTag();
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-burgundy-500"
                  placeholder="Añadir etiqueta"
                />
                <button
                  type="button"
                  onClick={addTag}
                  className="px-4 py-2 bg-burgundy-600 text-white rounded-lg hover:bg-burgundy-700 transition-colors"
                >
                  <Tag className="w-4 h-4" />
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-burgundy-100 text-burgundy-800 rounded-full text-sm"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => removeTag(tag)}
                      className="text-burgundy-600 hover:text-burgundy-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={onCancel}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="flex items-center gap-2 px-6 py-2 bg-burgundy-600 text-white rounded-lg hover:bg-burgundy-700 transition-colors"
          >
            <Save className="w-4 h-4" />
            {resource ? 'Actualizar' : 'Crear'} Recurso
          </button>
        </div>
      </form>
    </div>
  );
};

export default ResourceEditor;
